import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription, timer } from 'rxjs';
import { ReservationService } from '../../../../services/reservation.service';
import {
  CHANNEL_ICONS,
  CHANNEL_LABELS,
  Reservation,
  ReservationChannel,
  requestDeadlineLabel,
} from '../../../../models/reservation';
import { formatInZone, timeLabel, toServiceDate } from '../../../../services/reservation-time.util';
import { displayUsPhone } from '../../../../utils/us-phone.util';

/** Under this long left, the deadline is shown as urgent. */
const URGENT_MINUTES = 30;

/**
 * The Requests view on the book (FNF-308): every pending booking request
 * across all dates, soonest deadline first, with Accept and Decline. The day
 * view only shows one date's pending chip; this is where nothing is missed.
 *
 * Reads `ReservationService.pendingRequests$`, which the sidebar's poll, every
 * write and the RESERVATION_REQUESTED push keep fresh. Accept, Decline and
 * opening a row are handed to the page, which already confirms, declines
 * (with a reason) and reports how the guest was told.
 */
@Component({
  selector: 'app-request-queue',
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  templateUrl: './request-queue.component.html',
  styleUrl: './request-queue.component.css',
})
export class RequestQueueComponent implements OnInit, OnDestroy {
  private readonly reservationService = inject(ReservationService);

  /** The restaurant's IANA zone; every time is shown on its clock. */
  @Input() timeZone: string | undefined;

  @Output() accept = new EventEmitter<Reservation>();
  @Output() decline = new EventEmitter<Reservation>();
  /** Open the booking on its own day in the book. */
  @Output() open = new EventEmitter<Reservation>();

  readonly channelLabels = CHANNEL_LABELS;
  requests: Reservation[] = [];
  loading = true;
  failed = false;
  /** Re-read every 30 s so the countdowns move without a refetch. */
  now = Date.now();

  private subs = new Subscription();

  ngOnInit(): void {
    this.subs.add(
      this.reservationService.pendingRequests$.subscribe((rows) => (this.requests = rows)),
    );
    this.subs.add(timer(30_000, 30_000).subscribe(() => (this.now = Date.now())));
    this.reload();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  reload(): void {
    this.loading = !this.requests.length;
    this.failed = false;
    this.reservationService.loadRequests().subscribe({
      next: () => {
        this.loading = false;
        this.now = Date.now();
      },
      error: () => {
        this.loading = false;
        this.failed = true;
      },
    });
  }

  /** "Today · 7:30 PM", "Tomorrow · 7:30 PM" or "Sat 31 Oct · 7:30 PM". */
  whenOf(r: Reservation): string {
    const start = new Date(r.startAt);
    const today = toServiceDate(new Date(this.now), this.timeZone);
    const tomorrow = toServiceDate(new Date(this.now + 86_400_000), this.timeZone);
    const day =
      r.serviceDate === today
        ? 'Today'
        : r.serviceDate === tomorrow
          ? 'Tomorrow'
          : formatInZone(start, this.timeZone, {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
            });
    return `${day} · ${timeLabel(start, this.timeZone)}`;
  }

  deadlineOf(r: Reservation): string | null {
    return requestDeadlineLabel(r.expiresAt, this.now);
  }

  /** Little time left, or none: the host should answer this one first. */
  urgent(r: Reservation): boolean {
    if (!r.expiresAt) {
      return false;
    }
    return new Date(r.expiresAt).getTime() - this.now < URGENT_MINUTES * 60_000;
  }

  /** The deadline on the restaurant's clock, for the tooltip. */
  deadlineAt(r: Reservation): string {
    if (!r.expiresAt) {
      return '';
    }
    return (
      'Declined automatically at ' +
      formatInZone(new Date(r.expiresAt), this.timeZone, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
      })
    );
  }

  contactOf(r: Reservation): string {
    return displayUsPhone(r.guest.phone) || r.guest.email || '—';
  }

  channelIcon(channel: ReservationChannel): string {
    return CHANNEL_ICONS[channel] ?? 'bx-desktop';
  }
}
